import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { concatMap, filter, map, mergeMap, switchMap } from "rxjs/operators";
import {
  AnalysisDetailInterface,
  AnalysisGroupInterface,
  AnalysisInterface,
} from "src/app/modelli/inwit/analisi/analysis.interface";
import { AnalysisQueryPointInterface } from "src/app/modelli/inwit/analisi/query.interface";
import { RelAnalysisGroupInterface } from "src/app/modelli/inwit/analisi/rel-analysis-group.interface";
import { APIResponseInterface } from "src/app/modelli/inwit/api-response.interface";
import { GestioneLoginService } from "../login/gestione-login.service";
import { GestioneUtentiService } from "../utenti/gestione-utenti.service";
import { UtilsService } from "../utils.service";

@Injectable({
  providedIn: "root",
})
export class AnalysisDataAccessService {
  private analysisGetUrl = this.gestioneLoginService.creaURL("analysis", "get");
  private analysisUpdateUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "put"
  );
  private analysesUpdateUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "updateMassive"
  );
  private analysisAddUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "post"
  );
  private analysisRemoveUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "delete"
  );
  private analysesRemoveUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "removeMassive"
  );
  private analysesExportUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "export"
  );

  private analysesGetListUrlReduced = this.gestioneLoginService.creaURL(
    "analysis",
    "getListReduced"
  );
  private analysesGetListUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "getList"
  );
  private analysesGetElencoUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "getElenco"
  );
  private analysisExecuteUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "execute"
  );
  private analysesGetListPaginationUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "getListPagination"
  );
  private analysesGetAutocompleteDataUrl = this.gestioneLoginService.creaURL(
    "analysis",
    "getAutocompleteData"
  );

  private analysisAreaFabbricatiSearchSelectedPointsUrl =
    this.gestioneLoginService.creaURL("analysis", "fabbricatiSearch");
  private analysisAreaFabbricatiSearchSelectedPointsExecuteUrl =
    this.gestioneLoginService.creaURL("analysis", "fabbricatiSearchExecute");

  private analysisAddQueryPointUrl = this.gestioneLoginService.creaURL(
    "query",
    "post"
  );
  private analysisUpdateQueryPointUrl = this.gestioneLoginService.creaURL(
    "query",
    "put"
  );
  private analysisRemoveQueryPointUrl = this.gestioneLoginService.creaURL(
    "query",
    "delete"
  );
  private analysisDisableQueryPoint = this.gestioneLoginService.creaURL(
    "query",
    "disable"
  );

  private relAnalysisGroupAddUrl = this.gestioneLoginService.creaURL(
    "rel_analysis_group",
    "post"
  );
  private relAnalysesGroupMassiveUpdateUrl = this.gestioneLoginService.creaURL(
    "rel_analysis_group",
    "massiveUpdate"
  );
  private relAnalysisGroupDeleteUrl = this.gestioneLoginService.creaURL(
    "rel_analysis_group",
    "delete"
  );
  private resultsAddUrl = this.gestioneLoginService.creaURL("result", "add");
  private resultsGetUrl = this.gestioneLoginService.creaURL("result", "get");
  private resultsListUrl = this.gestioneLoginService.creaURL("result", "list");

  private getReportUrnUrl = this.gestioneLoginService.creaURL(
    "restrictedArea",
    "reportByUrns"
  );

  private uploadDocumentUrl = this.gestioneLoginService.creaURL(
    "document",
    "upload"
  );
  private downloadDocumentUrl = this.gestioneLoginService.creaURL(
    "document",
    "download"
  );
  private documentRemoveUrl = this.gestioneLoginService.creaURL(
    "document",
    "remove"
  );

  private kmzToKmlUrl = this.gestioneLoginService.creaURL("files", "kmzToKml");

  private getGroupUrl = this.gestioneLoginService.creaURL("group", "get");

  private analyses;

  constructor(
    private gestioneLoginService: GestioneLoginService,
    private gestioneUtentiService: GestioneUtentiService,
    private utilsService: UtilsService
  ) {}

  // setAnalysesObject(analyses: any){
  //   this.analyses = analyses;
  // }

  // getAnalysesObject(){
  //   return this.analyses;
  // }
  addRelAnalysisGroup(rel_analysis_group: RelAnalysisGroupInterface | void) {
    return of(rel_analysis_group).pipe(
      map((rel_analysis_group) => ({
        rel_analysis_group: { ...rel_analysis_group },
      })),
      switchMap(
        (body) =>
          this.utilsService.postRequest(
            this.relAnalysisGroupAddUrl,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  addAnalysis(analysis: AnalysisInterface) {
    return of(analysis).pipe(
      map((analysis) => ({
        analysis: { ...analysis },
      })),
      switchMap(
        (body) =>
          this.utilsService.postRequest(
            this.analysisAddUrl,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  getAnalysis(
    id: string
  ): Observable<APIResponseInterface<AnalysisDetailInterface[]>> {
    return of(id).pipe(
      filter((id) => !!id),
      map((id) => ({ ids: [id] })),
      switchMap(
        (params) =>
          this.utilsService.getRequest(
            this.analysisGetUrl,
            params
          ) as Observable<APIResponseInterface<AnalysisDetailInterface[]>>
      )
    );
  }

  getAnalyses(
    userId: string,
    group: ReadonlyArray<string | number>,
    attributes: string[] = undefined,
    reduced: boolean = false
  ): Observable<APIResponseInterface<AnalysisGroupInterface[]>> {
    return of({ userId, group, attributes }).pipe(
      filter((userInfo) => !!userInfo.userId),
      map((userInfo) => ({
        user: [userInfo.userId],
        group: userInfo.group,
        attributes: userInfo.attributes,
      })),
      switchMap(
        (params) =>
          this.utilsService.getRequest(
            reduced ? this.analysesGetListUrlReduced : this.analysesGetListUrl,
            params
          ) as Observable<APIResponseInterface<AnalysisGroupInterface[]>>
      )
    );
  }

  // Uso l'API già esistente della lista per recuperare dati esterni alla lista
  analysisGetElenco(params: any): Observable<any> {
    return this.utilsService.postRequest(this.analysesGetElencoUrl, params);
  }

  getAnalysesListOfLoggedUser(
    reduced = false,
    attributes?: string[]
  ): Observable<APIResponseInterface<AnalysisGroupInterface[]>> {
    var groups = [];
    var attr = !!attributes
      ? attributes
      : [
          "id",
          "user_id",
          "piano_lavori",
          "anno_piano_lavori",
          "nome_sito",
          "colore",
          "sr",
          "codice_cliente_1",
          "codice_cliente_2",
          "codice_cliente_3",
          "altezza_centro_elettrico_m",
          "raggio_area_di_ricerca_m",
          "lat",
          "lon",
        ];

    this.gestioneUtentiService.userData.groups.forEach((group) => {
      groups.push(group.id);
    });

    return this.getAnalyses(
      this.gestioneUtentiService.userData.id,
      groups,
      attr,
      reduced
    );
  }

  /**
   * @example params
   *
   * {
   *  "pagination": {"reqPage": 1, "rowsPerPage": 10},
   *  "where":
   *   {
   *     "user": "100ddfa1-47ea-4c78-90c0-bb3e15040261",
   *     "group":[9,10]
   *   }
   * }
   */
  getAnalysesListPaginationOfLoggedUser(
    reqPage,
    rowsPerPage
  ): Observable<APIResponseInterface<any>> {
    var groups = [];

    this.gestioneUtentiService.userData.groups.forEach((group) => {
      groups.push(group.id);
    });

    var params = {
      pagination: { reqPage: reqPage, rowsPerPage: rowsPerPage },
      where: {
        user: this.gestioneUtentiService.userData.id,
        group: groups,
      },
    };

    return this.utilsService.postRequest(
      this.analysesGetListPaginationUrl,
      params
    ) as Observable<APIResponseInterface<any>>;
  }

  getCompleteAnalysesOfLoggedUser(): Observable<
    APIResponseInterface<AnalysisGroupInterface[]>
  > {
    var groups = [];

    this.gestioneUtentiService.userData.groups.forEach((group) => {
      groups.push(group.id);
    });

    return this.getAnalyses(this.gestioneUtentiService.userData.id, groups);
  }

  updateAnalysis(
    analysis: AnalysisInterface
  ): Observable<APIResponseInterface<AnalysisInterface[]>> {
    return of(analysis).pipe(
      filter((analysis) => !!analysis.id),
      map((analysis) => ({
        analysis: { ...analysis },
      })),
      switchMap(
        (body) =>
          this.utilsService.putRequest(
            this.analysisUpdateUrl,
            body
          ) as Observable<APIResponseInterface<AnalysisInterface[]>>
      )
    );
  }

  //modifica massiva analisi
  updateAnalyses(obj: {
    ids: string[];
    data;
  }): Observable<APIResponseInterface<any>> {
    return this.utilsService.putRequest(
      this.analysesUpdateUrl,
      obj
    ) as Observable<APIResponseInterface<any>>;
  }

  // Associazione massiva Gruppi/Analisi
  relAnalysesGroupMassiveUpdate(obj: {
    analysisIds: number[];
    groupIds: number[];
  }): Observable<APIResponseInterface<any>> {
    return this.utilsService.postRequest(
      this.relAnalysesGroupMassiveUpdateUrl,
      obj
    ) as Observable<APIResponseInterface<any>>;
  }

  removeAnalysis(analysis: {
    id: number;
  }): Observable<APIResponseInterface<{}>> {
    return of(analysis).pipe(
      filter((analysis) => !!analysis.id),
      map((body) => ({
        analysis: { ...body },
      })),
      concatMap(
        (body) =>
          this.utilsService.deleteRequest(
            this.analysisRemoveUrl,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  removeAnalyses(obj: {
    ids: string[];
  }): Observable<APIResponseInterface<any>> {
    return this.utilsService.putRequest(
      this.analysesRemoveUrl,
      obj
    ) as Observable<APIResponseInterface<any>>;
  }

  removeDocument(document: {
    id: number;
  }): Observable<APIResponseInterface<{}>> {
    return of(document).pipe(
      filter((document) => !!document.id),
      map((body) => ({
        document: { ...body },
      })),
      concatMap(
        (body) =>
          this.utilsService.deleteRequest(
            this.documentRemoveUrl,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  addQuery(queryPoint: AnalysisQueryPointInterface) {
    return of(queryPoint).pipe(
      filter((queryPoint) => !!queryPoint.analysis_id),
      map((queryPoint) => ({
        query: { ...queryPoint },
      })),
      switchMap(
        (body) =>
          this.utilsService.postRequest(
            this.analysisAddQueryPointUrl,
            body
          ) as Observable<APIResponseInterface<AnalysisQueryPointInterface>>
      )
    );
  }

  updateQuery(
    query: AnalysisQueryPointInterface
  ): Observable<APIResponseInterface<{}>> {
    return of(query).pipe(
      filter((query) => !!query.analysis_id && !!query.id),
      map((query) => ({
        query: { ...query },
      })),
      mergeMap(
        (body) =>
          this.utilsService.putRequest(
            this.analysisUpdateQueryPointUrl,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  disableQuery(
    query: AnalysisQueryPointInterface
  ): Observable<APIResponseInterface<{}>> {
    return of(query).pipe(
      filter((query) => !!query.analysis_id),
      mergeMap(
        (body) =>
          this.utilsService.putRequest(
            this.analysisDisableQueryPoint,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  analysisExecute(param) {
    return this.utilsService.postRequest(this.analysisExecuteUrl, param);
  }

  listResult(params) {
    return this.utilsService.getRequest(this.resultsListUrl, params);
  }

  areaFabbricatiSearchSelectedPointsExecute(
    body
  ): Observable<APIResponseInterface<[]>> {
    return this.utilsService.postRequest(
      this.analysisAreaFabbricatiSearchSelectedPointsExecuteUrl,
      body
    ) as Observable<APIResponseInterface<[]>>;
  }

  getReportByUrns(params) {
    return this.utilsService.getRequest(this.getReportUrnUrl, params);
  }

  removeAnalysisQueryPoint(queryPoint: {
    [id: string]: number;
  }): Observable<APIResponseInterface<{}>> {
    return of(queryPoint).pipe(
      filter((queryPoint) => !!queryPoint.id),
      map((body) => ({
        query: { ...body },
      })),
      concatMap(
        (body) =>
          this.utilsService.deleteRequest(
            this.analysisRemoveQueryPointUrl,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  /**
   * Chiamata Rest APi per l'upload del file con reportProgress
   * @param attachedFile : input di tipo file
   */
  uploadDocument(param, id?) {
    let formData = new FormData();
    formData.append("file", param.file);
    formData.append("turnover", param.turnover.toString());
    formData.append("maxNumber", param.maxNum.toString());
    formData.append("funzionalita", param.funzionalita);
    formData.append("last_result_id", id);
    // console.log("FORMDATA", formData)

    return this.utilsService.postRequestFormData(
      this.uploadDocumentUrl,
      formData
    );
  }

  kmzToKmlConversion(param) {
    let formData = new FormData();
    formData.append("file", param);
    return this.utilsService.postRequestFormDataXml(this.kmzToKmlUrl, formData);
  }

  downloadDocument(fileName, path) {
    const formData = new FormData();
    formData.append("fileName", fileName);
    formData.append("path", path);
    return this.utilsService.postRequest(this.downloadDocumentUrl, formData);
  }

  removeAnalysisGroup(relations: {
    [id: string]: number;
  }): Observable<APIResponseInterface<{}>> {
    return of(relations).pipe(
      filter(
        (relations) =>
          !!relations.group_id && !!relations.analysis_id && !!relations.user_id
      ),
      map((body) => ({
        relations: { ...body },
      })),
      concatMap(
        (body) =>
          this.utilsService.deleteRequest(
            this.relAnalysisGroupDeleteUrl,
            body
          ) as Observable<APIResponseInterface<{}>>
      )
    );
  }

  getGroup(params): Observable<any> {
    return this.utilsService.getRequest(this.getGroupUrl, params);
  }

  public exportCsv(params): Observable<any> {
    return this.utilsService.postRequest(this.analysesExportUrl, params, true, {
      responseType: "blob",
    });
  }

  /**
   * @author Giulia Briscolini
   * @param params params = {
      dbField: ["DISTINCT piano_lavori"]
    }
   * @returns 
   */
  getAutocompleteData(params): Observable<any> {
    {
      return this.utilsService.postRequest(
        this.analysesGetAutocompleteDataUrl,
        params
      );
    }
  }
}
